import React, { ReactNode } from 'react';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import { SecondaryButton } from 'src/shared/components/button/Button';
import CMSArticle from 'src/shared/components/cms-article/CMSArticle';
import Modal from 'src/shared/components/modal/Modal';
import usePortfolioAnalytics from 'src/app/portfolio/views/portfolio-summary/hooks/usePortfolioAnalytics';
import styles from './SummaryCard.module.scss';

type SummaryCardProps = {
    cardIcon: ReactNode;
    cardTitle: ReactNode;
    cardSubtitle?: string;
    cardBody: ReactNode;
    titleExplainerSlug?: { defaultSlug: string; explainerSlug: string };
    action?: ReactNode;
    containerClasses?: string;
    frameworks?: string[];
    /** For E2E and automation — applied to the card root. */
    dataTestId?: string;
};

const SummaryCard = ({
    cardIcon,
    cardTitle,
    cardSubtitle,
    cardBody,
    titleExplainerSlug,
    action,
    frameworks,
    containerClasses = 'gap-1 p-4',
    dataTestId,
}: SummaryCardProps) => {
    const [selectedFramework, setSelectedFramework] = React.useState<string | null>(null);

    const trackAnalytics = usePortfolioAnalytics();

    return (
        <>
            <div
                data-testid={dataTestId}
                className={`flex-1 flex self-stretch flex-col rounded-lg shadow-portfolio-summary-card ${styles.summaryCard} ${containerClasses}`}
            >
                <div className='flex place-content-between'>
                    <div className='flex gap-2 items-center'>
                        <div className={styles.iconGradient}>{cardIcon}</div>
                        <div className='flex text-[17px] text-primary font-normal items-center'>
                            {cardTitle}
                            {titleExplainerSlug && (
                                <QuestionButtonHelp
                                    type='api'
                                    placement='right'
                                    slug={titleExplainerSlug.defaultSlug}
                                    detailedExplainerSlug={titleExplainerSlug.explainerSlug}
                                    onTooltipHover={() => trackAnalytics.trackExplainerHover({ slug: titleExplainerSlug.defaultSlug })}
                                    onDetailedHelpClick={() =>
                                        trackAnalytics.trackDetailedExplainerClick({ slug: titleExplainerSlug.explainerSlug })
                                    }
                                />
                            )}
                        </div>
                    </div>
                    {action}
                </div>
                {cardSubtitle && <div className='text-sm text-black font-normal mt-1 mb-2'>{cardSubtitle}</div>}
                {cardBody}
                {frameworks && frameworks.length > 0 && (
                    <div className='flex flex-col border-t border-grey-20 pt-[6px]'>
                        <div className='text-xs text-grey-80 mb-[6px]'>Reporting frameworks:</div>
                        <div className='flex gap-1'>
                            {frameworks.map(framework => (
                                <SecondaryButton
                                    key={framework}
                                    className='flex items-center rounded-lg text-sm h-8'
                                    onClick={() => {
                                        setSelectedFramework(framework);
                                        trackAnalytics.trackFrameworkClick();
                                    }}
                                >
                                    {framework}
                                </SecondaryButton>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {selectedFramework && (
                <Modal
                    onClose={() => {
                        setSelectedFramework(null);
                    }}
                    dismissOnClickOutside={true}
                    classNames={{
                        body: styles.reportingFrameworksModal,
                    }}
                >
                    <CMSArticle shouldShowTitle={true} slug={`portfolio-${selectedFramework.toLowerCase()}-framework`} />
                </Modal>
            )}
        </>
    );
};

export default SummaryCard;
